import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { superadminAPI } from '../../services/api'
import { useAlerts } from '../../contexts/AlertContext'
import {
  HiOutlineArrowLeft,
  HiOutlineUser,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineGlobeAlt,
  HiOutlineUsers,
  HiOutlineBriefcase,
  HiOutlineDocumentText,
  HiOutlineServerStack,
  HiOutlineClock,
  HiOutlinePause,
  HiOutlinePlay,
  HiOutlineXCircle,
  HiOutlineTrash
} from 'react-icons/hi2'

const UserDetailPage = () => {
  const { accountId } = useParams()
  const navigate = useNavigate()
  const { showSuccess, showError } = useAlerts()
  const [loading, setLoading] = useState(true)
  const [accountDetails, setAccountDetails] = useState(null)
  const [showActionModal, setShowActionModal] = useState(false)
  const [actionType, setActionType] = useState('')
  const [actionReason, setActionReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    if (accountId) {
      fetchAccountDetails()
    }
  }, [accountId])

  const fetchAccountDetails = async () => {
    try {
      setLoading(true)
      const response = await superadminAPI.getAccountDetail(accountId)
      
      if (response.success) {
        setAccountDetails(response.data.data)
      } else {
        showError('Failed to load account details')
      }
    } catch (error) {
      console.error('Error fetching account details:', error)
      showError('Failed to load account details')
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async () => {
    try {
      setActionLoading(true)
      const response = await superadminAPI.accountAction(
        accountId,
        actionType,
        actionReason
      )
      
      if (response.success) {
        const actionMessages = {
          'suspend': 'suspended',
          'reactivate': 'reactivated',
          'delete': 'deleted'
        }
        showSuccess(`Account ${actionMessages[actionType]} successfully`)
        setShowActionModal(false)
        setActionType('')
        setActionReason('')
        
        if (actionType === 'delete') {
          // Navigate back to subscribers list after deletion
          navigate('/superadmin/subscribers')
        } else {
          fetchAccountDetails()
        }
      } else {
        showError(response.error?.message || 'Action failed')
      }
    } catch (error) {
      console.error('Error performing action:', error)
      showError('Action failed')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  if (!accountDetails) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Account not found</p>
        <button
          onClick={() => navigate('/superadmin/subscribers')}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Back to Subscribers
        </button>
      </div>
    )
  }

  const { account, subscription, storage, activity, employees, connected_to } = accountDetails

  const getStatusBadge = () => {
    if (account.is_deleted) {
      return <span className="px-3 py-1 text-sm font-medium bg-red-100 text-danger rounded-full">Deleted</span>
    }
    if (account.is_disabled) {
      return <span className="px-3 py-1 text-sm font-medium bg-amber-100 text-warning rounded-full">Suspended</span>
    }
    if (account.is_active) {
      return <span className="px-3 py-1 text-sm font-medium bg-green-100 text-success rounded-full">Active</span>
    }
    return <span className="px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-full">Inactive</span>
  }

  const StatCard = ({ icon: Icon, label, value, color = 'blue' }) => (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <div className={`p-2 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/superadmin/subscribers')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <HiOutlineArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{account.name}</h1>
            <p className="text-gray-600 mt-1">Account ID: {account.id}</p>
          </div>
          {getStatusBadge()}
        </div>
        
        <div className="flex gap-2">
          {account.is_deleted ? (
            <span className="px-4 py-2 bg-red-100 text-danger rounded-lg font-medium">
              Account Deleted
            </span>
          ) : account.is_disabled ? (
            <button
              onClick={() => {
                setActionType('reactivate')
                setShowActionModal(true)
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <HiOutlinePlay className="w-5 h-5" />
              Reactivate
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  setActionType('suspend')
                  setShowActionModal(true)
                }}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2"
              >
                <HiOutlinePause className="w-5 h-5" />
                Suspend
              </button>
              <button
                onClick={() => {
                  setActionType('delete')
                  setShowActionModal(true)
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <HiOutlineTrash className="w-5 h-5" />
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Owner Information Card */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Owner Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <HiOutlineUser className="w-5 h-5 text-brand" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Full Name</div>
              <div className="font-medium text-gray-900">{account.owner.name}</div>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="p-2 bg-brand-soft rounded-lg">
              <HiOutlineEnvelope className="w-5 h-5 text-brand" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Email</div>
              <div className="font-medium text-gray-900">{account.owner.email}</div>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <HiOutlinePhone className="w-5 h-5 text-success" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Mobile</div>
              <div className="font-medium text-gray-900">{account.owner.mobile}</div>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <HiOutlineGlobeAlt className="w-5 h-5 text-warning" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Country</div>
              <div className="font-medium text-gray-900">{account.owner.country?.name || 'N/A'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={HiOutlineUsers} label="Total Clients" value={activity.total_clients} color="blue" />
        <StatCard icon={HiOutlineBriefcase} label="Total Cases" value={activity.total_cases} color="purple" />
        <StatCard icon={HiOutlineClock} label="Total Sessions" value={activity.total_sessions} color="green" />
        <StatCard icon={HiOutlineDocumentText} label="Total Documents" value={activity.total_documents} color="amber" />
      </div>

      {/* Storage and Subscription */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Storage */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <HiOutlineServerStack className="w-6 h-6 text-indigo-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Storage Usage</h2>
          </div>
          
          <div className="space-y-3">
            <div>
              <div className="text-3xl font-bold text-gray-900">{storage.total_gb} GB</div>
              <div className="text-sm text-gray-600">Total storage used</div>
            </div>
            
            {storage.limit_bytes && (
              <>
                <div className="text-sm text-gray-600">
                  Limit: {(storage.limit_bytes / (1024 ** 3)).toFixed(2)} GB
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-indigo-600 h-2 rounded-full"
                    style={{ 
                      width: `${Math.min((storage.total_bytes / storage.limit_bytes) * 100, 100)}%` 
                    }}
                  />
                </div>
                <div className="text-xs text-gray-500">
                  {((storage.total_bytes / storage.limit_bytes) * 100).toFixed(1)}% used
                </div>
              </>
            )}
          </div>
        </div>

        {/* Subscription */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h2>
          
          {subscription.current ? (
            <div className="space-y-3">
              <div>
                <div className="text-sm text-gray-500">Plan</div>
                <div className="text-lg font-semibold text-gray-900">{subscription.current.plan_name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Status</div>
                <div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    subscription.current.status === 'active' 
                      ? 'bg-green-100 text-success'
                      : subscription.current.status === 'expired'
                      ? 'bg-red-100 text-danger'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {subscription.current.status}
                  </span>
                </div>
              </div>
              {subscription.current.expiration_date && (
                <div>
                  <div className="text-sm text-gray-500">Expires</div>
                  <div className="text-gray-900">
                    {new Date(subscription.current.expiration_date).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-600">No active subscription</p>
          )}
        </div>
      </div>

      {/* Activity Information */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-sm text-gray-500">Account Created</div>
            <div className="font-medium text-gray-900">
              {new Date(account.created_at).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Last Activity</div>
            <div className="font-medium text-gray-900">
              {activity.last_activity 
                ? new Date(activity.last_activity).toLocaleString()
                : 'Never'}
            </div>
          </div>
          {account.is_disabled && account.disabled_at && (
            <div>
              <div className="text-sm text-gray-500">Suspended At</div>
              <div className="font-medium text-gray-900">
                {new Date(account.disabled_at).toLocaleString()}
              </div>
            </div>
          )}
        </div>
        
        {account.disabled_reason && (
          <div className="mt-4 p-4 bg-warning-soft rounded-lg">
            <div className="text-sm font-medium text-amber-900 mb-1">Suspension Reason:</div>
            <div className="text-sm text-warning">{account.disabled_reason}</div>
          </div>
        )}
        
        {account.deleted_reason && (
          <div className="mt-4 p-4 bg-danger-soft rounded-lg">
            <div className="text-sm font-medium text-red-900 mb-1">Deletion Reason:</div>
            <div className="text-sm text-danger">{account.deleted_reason}</div>
          </div>
        )}
      </div>

      {/* Employees/Followers */}
      {employees && employees.count > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Employees/Followers ({employees.count})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Connected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {employees.list.map((emp, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{emp.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{emp.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{emp.mobile}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{emp.country?.name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 bg-blue-100 text-brand rounded-full text-xs font-medium">
                        {emp.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {emp.is_active ? (
                        <span className="text-success font-medium">Active</span>
                      ) : (
                        <span className="text-gray-400 font-medium">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(emp.connected_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Connected Workspaces (if this user is an employee) */}
      {connected_to && connected_to.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Connected to Workspaces ({connected_to.length})
          </h2>
          <div className="space-y-3">
            {connected_to.map((workspace, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{workspace.receiver_account__name}</div>
                  <div className="text-sm text-gray-600">Owner: {workspace.receiver_account__owner__name}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">Role: {workspace.assigned_role}</div>
                  <div className="text-xs text-gray-500">
                    Connected: {new Date(workspace.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscription History */}
      {subscription.history && subscription.history.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription History</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscription.history.map((sub, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3">{sub.plan_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        sub.status === 'active' 
                          ? 'bg-green-100 text-success'
                          : sub.status === 'expired'
                          ? 'bg-red-100 text-danger'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {new Date(sub.start_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {sub.expiration_date 
                        ? new Date(sub.expiration_date).toLocaleDateString()
                        : 'No expiration'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {actionType === 'suspend' && 'Suspend Account'}
              {actionType === 'reactivate' && 'Reactivate Account'}
              {actionType === 'delete' && 'Delete Account'}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Account: <span className="font-medium text-gray-900">{account.name}</span>
              </p>
              <p className="text-sm text-gray-600">
                Owner: <span className="font-medium text-gray-900">{account.owner.name}</span>
              </p>
            </div>

            {actionType === 'delete' && (
              <div className="mb-4 p-4 bg-danger-soft border border-danger/30 rounded-lg">
                <p className="text-sm text-red-800 font-medium">⚠️ Warning</p>
                <p className="text-sm text-danger mt-1">
                  This will delete the account and prevent the user from accessing any data. This action is reversible only by a superadmin.
                </p>
              </div>
            )}

            {(actionType === 'suspend' || actionType === 'delete') && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason {actionType === 'delete' ? '(required)' : '(optional)'}
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder={`Enter reason for ${actionType}...`}
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleAction}
                disabled={actionLoading || (actionType === 'delete' && !actionReason.trim())}
                className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${
                  actionType === 'suspend'
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : actionType === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {actionLoading ? 'Processing...' : 'Confirm'}
              </button>
              <button
                onClick={() => {
                  setShowActionModal(false)
                  setActionType('')
                  setActionReason('')
                }}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserDetailPage

